import type { ExpertFormData } from '../../types';
import ExpertIntegrationContainer from '../ExpertIntegrationContainer';
import { GoogleDrive } from '@/client/components/icons/ContinuIcons';
import GoogleDriveUploadFileList from './GoogleDriveUploadFileList';
import GooglePicker from '@/client/components/media/GooglePicker';
import { ExtensionType, IntegrationSourceType } from '@/client/services/api/graphql/gql/graphql';
import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import S3Service from '@/client/services/api/aws/S3Service';
import { getGoogleDriveIntegrationStatus } from '@/client/services/api/integrations/GoogleService';
import { Stack } from '@chakra-ui/react';
import axios from 'axios';
import { getExtensionTypeFromFile } from '@/client/utils/type-maps/extensionTypeFromFile';
import { useAuthStore } from '@/client/services/state/authStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useExpertsStore } from '../../../expertsStore';
import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import {
  createDownloadLink,
  googleNativeMimeTypeMap,
  isGoogleNativeFormat,
} from '@/client/utils/google/createLocationLink';
import { useConfigStore } from '@/client/services/state/configStore';
import ExpertsService from '@/client/services/api/ExpertsService';

export default function ExpertGoogleDriveIntegrationForm() {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { config } = useConfigStore();
  const { setToast } = useToastStore();
  const { isEditingExpert: isEditing, setIsSyncingExpert: setIsSyncing } = useExpertsStore();
  const { watch, setValue, getValues } = useFormContext<ExpertFormData>();
  const { googleDriveIntegration, resources, id } = watch();
  const [googleDriveIntegrationInProgress, setGoogleDriveIntegrationInProgress] = useState(false);
  const [lastSynced, setLastSynced] = useState<string | undefined>(undefined);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setPickerOpen(false);
    }
  }, [isEditing]);

  // Poll for integration status every 5 seconds if integration is in progress
  const { data: integrationData } = useQuery({
    enabled: googleDriveIntegrationInProgress,
    queryKey: ['drive_integration_status', user._id],
    queryFn: () => getGoogleDriveIntegrationStatus(user._id),
    refetchInterval: googleDriveIntegrationInProgress ? 5000 : false,
  });

  useEffect(() => {
    if (integrationData && integrationData.integrated) {
      setValue('googleDriveIntegration', true);
      setGoogleDriveIntegrationInProgress(false);
    }
  }, [integrationData]);
  // ---------------

  const calculateLastSynced = () => {
    const googleDriveResources = resources?.filter(
      (resource) => resource.source === IntegrationSourceType.GoogleDrive,
    );
    if (!googleDriveResources || googleDriveResources.length === 0) {
      return undefined;
    }
    return googleDriveResources.reduce((latest, resource) => {
      if (resource.lastSynced > latest) {
        return resource.lastSynced;
      }
      return latest;
    }, googleDriveResources[0].lastSynced);
  };

  useEffect(() => {
    if (resources) {
      setLastSynced(calculateLastSynced());
    }
  }, [id]);

  const handleIntegrationClick = async () => {
    const scopes = 'https://www.googleapis.com/auth/drive';
    setGoogleDriveIntegrationInProgress(true);
    window.open(
      `https://accounts.google.com/o/oauth2/v2/auth?scope=${scopes}&prompt=consent&access_type=offline&include_granted_scopes=true&response_type=code&&redirect_uri=https://${
        (config.host === 'local.continu.co'
          ? 'staging.continu.co'
          : config.host.indexOf('herokuapp') !== -1
          ? 'staging.continu.co'
          : config.host) + '/google-redirect'
      }&client_id=${config.google.clientId}`,
      '_blank',
    );
  };

  const getFileAndUploadToBucket = async (
    file: {
      id: string;
      auth: string;
      fileName: string;
      mimeType: string;
      isFolder: boolean;
      parentFolderId?: string;
    },
    currentFileCount: number,
  ) => {
    // TODO: See if we can cancel previous uploads if limit exceeded
    if (currentFileCount >= 20) {
      console.error(
        `Folder upload would exceed file limit of 20. Upload cancelled. Current file count: ${currentFileCount}`,
      );
      setToast({
        show: true,
        status: 'error',
        title: t('admin.aiAgent.experts.form.integrations.googleDrive.uploadFolderLimitError'),
      });
      return;
    }

      // TODO: See if we can cancel previous uploads if limit exceeded
      if (currentFileCount >= 20) {
        console.error(`Folder upload would exceed file limit of 20. Upload cancelled. Current file count: ${currentFileCount}`);
        setToast({
          show: true,
          status: 'error',
          title: t('admin.aiAgent.experts.form.integrations.googleDrive.uploadFolderLimitError'),
        });
        return;
      }
    
    // Recursively handle folder upload
      if (file.isFolder) {
        const folderChildren = await axios.get(createDownloadLink(file.id, file.mimeType, true), {
          headers: {
            Authorization: `Bearer ${file.auth}`,
          },
          params: {
            q: `'${file.id}' in parents and trashed = false`,
          }
        });

        const newFileCount = currentFileCount + folderChildren.data.items.length;

        // Keeps us from adding a folder that would exceed the 20 file limit
        if (newFileCount > 20) {
          console.error(`Folder upload would exceed file limit of 20. Upload cancelled. Current file count: ${currentFileCount}, files in folder: ${folderChildren.data.items.length}`);
          setToast({
            show: true,
            status: 'error',
            title: t('admin.aiAgent.experts.form.integrations.googleDrive.uploadFolderLimitError'),
          });
          return;
        }

        const folderFilesPromises = await Promise.all(folderChildren.data.items.map((childFile: any) => 
          getFileAndUploadToBucket({
            id: childFile.id,
            auth: file.auth,
            fileName: childFile.title,
            mimeType: childFile.mimeType,
            isFolder: childFile.mimeType === 'application/vnd.google-apps.folder',
            parentFolderId: file.id,
          }, newFileCount)
        ));
        return [{ 
          addedBy: user._id,
          downloadLink: folderChildren.data.selfLink,
          extensionName: file.fileName,
          extensionType: ExtensionType.Folder,
          id: '',
          source: IntegrationSourceType.GoogleDrive,
          sourceId: file.id,
          fileCount: folderChildren.data.items.length,
          isFolder: true,
          path: folderChildren.data.selfLink,
          ...(file.parentFolderId ? { parentFolder: file.parentFolderId } : {}),
        },
      ].concat(folderFilesPromises.flat());
    }

    const adjustedMimeType = isGoogleNativeFormat(file.mimeType)
      ? googleNativeMimeTypeMap[file.mimeType] || 'application/pdf'
      : file.mimeType;

    // Download the file from Google Drive
    const response = await axios.get(createDownloadLink(file.id, file.mimeType, false), {
      headers: {
        Authorization: `Bearer ${file.auth}`,
      },
      responseType: 'blob',
    });

    // transforming the downloaded file (bytes) into a Blob and then a File object to upload to S3
    const uploadedFile = await S3Service.uploadToBucket(
      new File([response.data], file.fileName, { type: adjustedMimeType }),
    );

    return [
      {
        addedBy: user._id,
        downloadLink: uploadedFile.cloudFrontLink,
        extensionName: file.fileName,
        extensionType: getExtensionTypeFromFile({
          type: adjustedMimeType,
          name: file.fileName,
        } as File),
        id: '',
        path: uploadedFile.cloudFrontLink,
        source: IntegrationSourceType.GoogleDrive,
        sourceId: file.id,
        isFolder: false,
        ...(file.parentFolderId ? { parentFolder: file.parentFolderId } : {}),
      },
    ];
  };

  const syncManually = useMutation({
    mutationFn: async (expertId: string) => {
      await ExpertsService.manuallySyncExpert(expertId);
      setIsSyncing(true);
    },
    onSuccess: () => {
      setToast({
        show: true,
        status: 'success',
        title: 'Sync initiated successfully',
      });
      setIsSyncing(false);
    },
    onError: () => {
      setToast({
        show: true,
        status: 'error',
        title: 'Error initiating sync',
      })
      setIsSyncing(false);
    }
  });

  const onSelect = async (
    selectedFiles: {
      id: string;
      auth: string;
      fileName: string;
      mimeType: string;
      isFolder: boolean;
    }[],
  ) => {
    try {
      setIsSyncing(true);
      const resourceFileCount =
        getValues('resources')?.filter((resource) => !resource.isFolder).length || 0;

      const files = await Promise.all(
        selectedFiles.map(async (file) => getFileAndUploadToBucket(file, resourceFileCount)),
      );

      const newResources = [
        ...(resources || []),
        ...files.flat().filter((file) => file !== undefined),
      ];

      setValue('resources', newResources, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: true,
      });
      setPickerOpen(false);
      setIsSyncing(false);
    } catch (error) {
      console.error('Error uploading files from Google Drive:', error);

      setPickerOpen(false);
      setIsSyncing(false);
      setToast({
        show: true,
        status: 'error',
        // TODO update error message
        title: t('admin.aiAgent.experts.form.integrations.localUpload.uploadError'),
      });
    }
  };

  return (
    <ExpertIntegrationContainer
      renderSetupButton={isEditing && !googleDriveIntegration}
      renderIntegrationStatus={!isEditing}
      integrationEnabled={googleDriveIntegration}
      handleOnSetupButtonClick={handleIntegrationClick}
      onManualSync={
        id &&
        resources.filter((resource) => resource.source === IntegrationSourceType.GoogleDrive).length
          ? () => syncManually.mutate(id)
          : undefined
      }
      lastSynced={lastSynced}
      integrationName={t('admin.aiAgent.experts.form.integrations.googleDrive.title')}
      integrationDescription={t('admin.aiAgent.experts.form.integrations.googleDrive.helpText')}
      integrationIcon={<GoogleDrive boxSize={8} />}
    >
      <Stack paddingTop={8} spacing={8}>
        {resources?.filter((resource) => resource.source === IntegrationSourceType.GoogleDrive)
          .length && <GoogleDriveUploadFileList />}

        {/* Temporarily hide add file button until integration is live */}
        {isEditing && googleDriveIntegration && (
          <OutlineButton
            onClick={() => setPickerOpen(true)}
            maxWidth="200px"
            disabled={!googleDriveIntegration}
          >
            Add File
          </OutlineButton>
        )}
      </Stack>

      {pickerOpen && <GooglePicker onClose={() => setPickerOpen(false)} onSelect={onSelect} />}
    </ExpertIntegrationContainer>
  );
}
